import type { PaymentFlowCancelEvent } from '@appercut/round-vue';
import { readWalletFailure, readWalletPaymentId, walletCloseEvent } from '../utils/walletCancel';

const WALLET_RETURN_TIMEOUT_MS = 60000;

export function useWalletLoader(options: {
  onWalletFailure?: (error: string) => Promise<void>
  onWalletAuthorized?: (paymentId: string | null) => Promise<boolean>
} = {}) {
  const { $logger } = useNuxtApp();
  const dialogStore = useDialogStore();
  const { track } = useEventsDispatcher();
  const { gatewayType } = useHelpers();
  const { rememberMethod, walletMethodType } = useHyperswitchRedirectReturn();

  let releaseWalletLoader: (() => void) | null = null;
  let walletStartedAt: number | null = null;
  let isCapturing = false;

  function onWalletClick(): void {
    rememberMethod('wallet');
    walletStartedAt = performance.now();

    const type = walletMethodType();
    track('Check-out Started', { type, gateway: gatewayType.value });
    track('Authorization_Start', { type, gateway: gatewayType.value });
  }

  function reportCaptureError(error: unknown): void {
    $logger.error(error as Error, { tags: { flow: 'payment', method: 'wallet', step: 'capture' } });
  }

  function captureInPlace(paymentId: string): void {
    isCapturing = true;

    Promise.resolve(options.onWalletAuthorized?.(paymentId))
      .catch((error: unknown) => {
        reportCaptureError(error);

        return false;
      })
      .then((hasMovedOn) => {
        isCapturing = false;

        if (!hasMovedOn) dialogStore.setLoader(false);
      });
  }

  function onWalletComplete(payload?: unknown): void {
    releaseWalletLoader?.();

    $logger.note({
      category: 'payment',
      message: 'the wallet flow reported completion',
      level: 'info',
      data: { fields: Object.keys((payload ?? {}) as Record<string, unknown>) },
    });

    const failure = readWalletFailure(payload);

    if (failure) {
      void options.onWalletFailure?.(failure);

      return;
    }

    dialogStore.setLoader(true);

    const paymentId = readWalletPaymentId(payload);

    if (paymentId) {
      captureInPlace(paymentId);

      return;
    }

    void options.onWalletAuthorized?.(null).catch(reportCaptureError);

    let hasLeftThePage = false;

    const stop = (): void => {
      dialogStore.setLoader(false);
      releaseWalletLoader?.();
    };
    const leave = (): void => {
      hasLeftThePage = true;
    };
    const stopAfterLeaving = (): void => {
      if (hasLeftThePage) stop();
    };
    const onVisibilityChange = (): void => {
      if (globalThis.document?.visibilityState !== 'visible') {
        hasLeftThePage = true;

        return;
      }

      stopAfterLeaving();
    };
    const timer = setTimeout(stop, WALLET_RETURN_TIMEOUT_MS);
    globalThis.addEventListener('blur', leave);
    globalThis.addEventListener('pagehide', leave);
    globalThis.addEventListener('focus', stopAfterLeaving);
    globalThis.addEventListener('pageshow', stopAfterLeaving);
    globalThis.document?.addEventListener('visibilitychange', onVisibilityChange);

    releaseWalletLoader = () => {
      clearTimeout(timer);
      globalThis.removeEventListener('blur', leave);
      globalThis.removeEventListener('pagehide', leave);
      globalThis.removeEventListener('focus', stopAfterLeaving);
      globalThis.removeEventListener('pageshow', stopAfterLeaving);
      globalThis.document?.removeEventListener('visibilitychange', onVisibilityChange);
      releaseWalletLoader = null;
    };
  }

  function onWalletCancel(event: PaymentFlowCancelEvent): void {
    releaseWalletLoader?.();

    if (!isCapturing) dialogStore.setLoader(false);

    const name = walletCloseEvent(event.paymentMethodType);
    const timeToCloseMs = walletStartedAt === null ? 0 : Math.round(performance.now() - walletStartedAt);
    walletStartedAt = null;

    if (!name) return;

    if (name === 'applepay_syscheeckout_closed') {
      track(name);

      return;
    }

    track(name, {
      reason: event.canRetryPayment ? 'user' : 'error',
      time_to_close_ms: timeToCloseMs,
    });
  }

  return { onWalletClick, onWalletComplete, onWalletCancel };
}
